/**
 * Prefix a root-relative path with its locale, the way Next.js routes it.
 *
 * The default locale routes without a prefix, every other locale routes under
 * `/<locale>`. That ternary was written out fresh in fifteen places, in two
 * incompatible shapes — `/${locale}${path}` adds the leading slash, while
 * `${locale}${path}` assumes `path` already carries one. Copy the wrong one into
 * the wrong context and you get `/nlblog/x` or `//nl/blog/x`.
 *
 * `path` is expected to start with `/`. Anything root-relative that reaches
 * `new URL(path, origin)` resolves the same either way, which is why both shapes
 * survived as long as they did.
 *
 * Lived in `src/features/search/utils/url.utils.ts` until WHO-5387; nothing
 * outside that feature imported it, most likely because nobody knew it existed.
 */
export const withLocale = (
  path: string,
  locale?: string,
  defaultLocale?: string
) => (locale && locale !== defaultLocale ? `/${locale}${path}` : path);

/**
 * Parse a root-relative path with `URL`, without borrowing a real origin.
 *
 * `URL` refuses a relative path unless it is given a base, so three call sites
 * passed `CANONICAL_HOST` purely to satisfy it and then sliced the origin back
 * off. That made them depend on site configuration they never used: WHO-5133
 * recorded that a blank `NEXT_PUBLIC_CANONICAL_DOMAIN` therefore threw
 * `TypeError: Invalid URL` inside `useURLPagination`, taking down pagination
 * across the site, for want of a query-string parser.
 *
 * The base is a reserved `.invalid` host (RFC 2606), so it can never resolve and
 * can never be mistaken for a real origin if one of these ever leaks it. Only
 * `pathname`, `search` and `searchParams` are meant to be read off the result.
 */
export const RELATIVE_URL_BASE = "https://relative.invalid";

export const parseRelativePath = (path: string) =>
  new URL(path, RELATIVE_URL_BASE);

/**
 * Inline script for `_document`: takes credentials out of the address bar
 * before anything can read it (WHO-6202).
 *
 * The reset email links to `?token=…&uid=…&cid=…`, the login-code email to
 * `?email=…&token=…&cookie=…`, and old native form submits left
 * `?email=…&password=…` in browser history (WHO-6151). GTM's GA4, Pinterest
 * and Ads tags read `document.location` themselves, so cleaning only our own
 * page-view event was not enough: GA4 stored these as `page_location` and
 * `page_referrer`. gtm.js loads after hydration (`lazyOnload` in `_app`), and
 * Next reads `location` when its bundle boots, so a `<head>` script runs
 * before every one of them.
 *
 * Works on parameter names, on any page: `token`, `uid`, `cid`, `cookie` and
 * anything containing `password`, plus `email` when one of those is there. A
 * URL with only `email` is left alone. `utm_*` and the rest stay. `cid` is
 * `sha256(email)`, which nothing on the web reads. What the pages need is kept
 * in memory only (`window.__urlSecrets`, read with `readUrlSecrets`), never in
 * storage. It stays there until the next full page load, so also after the
 * reset or login and after client-side navigation, and is gone on a refresh: a
 * refreshed reset page fails with the usual "update failed" message.
 */
export const STRIP_URL_SECRETS = `(function () {
  var params = new URLSearchParams(location.search);
  var keys = [];
  params.forEach(function (value, key) {
    if (/password|^(token|uid|cid|cookie)$/i.test(key)) keys.push(key);
  });
  if (!keys.length) return;
  var secrets = {};
  ["token", "uid", "cookie", "email"].forEach(function (key) {
    if (params.has(key)) secrets[key] = params.get(key);
  });
  keys.concat("email").forEach(function (key) {
    params.delete(key);
  });
  window.__urlSecrets = secrets;
  var search = params.toString();
  history.replaceState(history.state, "", location.pathname + (search ? "?" + search : "") + location.hash);
})();`;

type UrlSecrets = Partial<Record<"token" | "uid" | "cookie" | "email", string>>;

/** What `STRIP_URL_SECRETS` took out of this page load's URL. */
export const readUrlSecrets = (): UrlSecrets =>
  (window as Window & { __urlSecrets?: UrlSecrets }).__urlSecrets ?? {};
